const SCRIPT_ID = 'naver-maps-sdk';

let loadPromise: Promise<void> | null = null;

/**
 * 네이버 지도 SDK를 한 번만 로드하고, `window.naver.maps`가 실제로 준비된 뒤 resolve한다.
 *
 * next/script의 `onReady`를 쓰지 않는 이유:
 * 스크립트가 로드되는 도중에 컴포넌트가 재마운트되면 next/script는 그 인스턴스에
 * `onLoad`만 등록하고 `onReady`는 등록하지 않는다(`loadScript`의 ScriptCache 분기).
 * `onReady`를 호출하는 클로저는 최초 인스턴스에만 남아 있어, 이미 언마운트된 쪽만
 * 알림을 받고 실제로 화면에 있는 인스턴스는 로드 완료를 영영 통지받지 못한다.
 * 지점 목록 지도처럼 데이터 도착 시 재마운트되는 곳에서는 스크립트 로드가 느릴수록
 * (저사양 PC 등) 이 경로에 걸려 지도가 뜨지 않는다.
 *
 * 로드 상태를 모듈 스코프 Promise 하나로 관리하면 마운트 시점과 무관하게 항상
 * 로드 완료를 기다릴 수 있다.
 */
export function loadNaverMaps(clientId: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (window.naver?.maps) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    script.addEventListener('load', () => {
      if (window.naver?.maps) {
        resolve();
        return;
      }
      // 스크립트는 받았지만 SDK가 초기화되지 않은 경우(예: 인증 실패 응답)
      loadPromise = null;
      reject(new Error('네이버 지도 SDK가 초기화되지 않았습니다.'));
    });

    script.addEventListener('error', () => {
      // 다음 마운트에서 다시 시도할 수 있도록 되돌린다.
      loadPromise = null;
      script.remove();
      reject(new Error('네이버 지도 SDK를 불러오지 못했습니다.'));
    });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}
